import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  voidPublicElevatorSurveyPhoto,
  unvoidPublicElevatorSurveyPhoto,
  fetchPublicElevatorSurvey,
  publicElevatorSurveyPhotoUrl,
  submitPublicElevatorSurvey,
  uploadPublicElevatorSurveyPhoto,
  type ElevatorSurveyPhoto,
  type PublicElevatorSurveyInfo,
} from '../api/elevatorAppointments';
import { isVoided } from '../api/attachmentVoid';
import { ELEVATOR_SURVEY_FORM, ELEVATOR_SURVEY_PHOTOS } from '../data/elevatorSurveyForm';
import { generateElevatorSurveyPdf } from '../utils/elevatorSurveyPdf';
import FileDropZone from '../components/FileDropZone';

const wrapStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: '#f3f4f6',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '2.5rem 1rem',
  fontFamily: 'Arial, Helvetica, sans-serif',
  color: '#1b1c1e',
};
const cardStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: 12,
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  maxWidth: 720,
  width: '100%',
  padding: '2rem',
};
const labelStyle: React.CSSProperties = { display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' };
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.55rem 0.7rem',
  borderRadius: 8,
  border: '1px solid #cbd2d9',
  fontSize: '1rem',
  boxSizing: 'border-box',
};
const sectionTitleStyle: React.CSSProperties = {
  fontSize: '1.05rem',
  color: '#1f4b31',
  margin: '1.75rem 0 0.75rem',
  paddingBottom: '0.4rem',
  borderBottom: '2px solid #e2e5e9',
};
const photoCardStyle: React.CSSProperties = {
  border: '1px solid #dfe3e8',
  borderRadius: 10,
  padding: '0.85rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.6rem',
  background: '#fbfcfd',
};
const smallButtonStyle: React.CSSProperties = {
  background: 'white',
  border: '1px solid #cbd2d9',
  borderRadius: 6,
  padding: '0.35rem 0.7rem',
  fontSize: '0.85rem',
  cursor: 'pointer',
};
const primaryButtonStyle: React.CSSProperties = {
  background: '#2f6f44',
  color: 'white',
  border: 'none',
  borderRadius: 8,
  padding: '0.7rem 1.75rem',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const ElevatorSurvey: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<PublicElevatorSurveyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [photos, setPhotos] = useState<Record<string, ElevatorSurveyPhoto>>({});
  // Voided photos stay on the survey as a record of what was sent before, and the
  // slot they came from opens back up for a replacement.
  const [voidedPhotos, setVoidedPhotos] = useState<ElevatorSurveyPhoto[]>([]);
  const [voidSlot, setVoidSlot] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [photoSlot, setPhotoSlot] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmMissing, setConfirmMissing] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError('Invalid link.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchPublicElevatorSurvey(token)
      .then((data) => {
        if (cancelled) return;
        setInfo(data);
        const initial: Record<string, string> = {};
        for (const [k, v] of Object.entries(data.form_data || {})) {
          initial[k] = typeof v === 'string' ? v : v == null ? '' : String(v);
        }
        setValues(initial);
        const byField: Record<string, ElevatorSurveyPhoto> = {};
        const voided: ElevatorSurveyPhoto[] = [];
        for (const p of data.photos || []) {
          if (isVoided(p)) voided.push(p);
          else byField[p.field_id] = p;
        }
        setPhotos(byField);
        setVoidedPhotos(voided);
        if (data.already_submitted) setSubmitted(true);
      })
      .catch((e) => !cancelled && setLoadError(e instanceof Error ? e.message : 'Failed to load survey.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const missingPhotos = useMemo(
    () => ELEVATOR_SURVEY_PHOTOS.filter((p) => !photos[p.id]),
    [photos]
  );

  const setValue = (id: string, v: string) => setValues((prev) => ({ ...prev, [id]: v }));

  const handlePhotoFiles = async (fieldId: string, files: File[]) => {
    const file = files[0];
    if (!file || !token) return;
    setPhotoBusy(true);
    setPhotoError('');
    try {
      const saved = await uploadPublicElevatorSurveyPhoto(token, fieldId, file);
      setPhotos((prev) => ({ ...prev, [fieldId]: saved }));
      setPhotoSlot(null);
    } catch (e) {
      setPhotoError(e instanceof Error ? e.message : 'Failed to upload photo.');
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleVoidPhoto = async (fieldId: string) => {
    const photo = photos[fieldId];
    const reason = voidReason.trim();
    if (!photo || !token) return;
    if (!reason) {
      setPhotoError('Please say why this photo is being voided.');
      return;
    }
    setPhotoBusy(true);
    setPhotoError('');
    try {
      const saved = await voidPublicElevatorSurveyPhoto(token, photo.id, reason);
      setPhotos((prev) => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
      setVoidedPhotos((prev) => [...prev, saved]);
      setVoidSlot(null);
      setVoidReason('');
    } catch (e) {
      setPhotoError(e instanceof Error ? e.message : 'Failed to void photo.');
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleUnvoidPhoto = async (photo: ElevatorSurveyPhoto) => {
    if (!token) return;
    setPhotoBusy(true);
    setPhotoError('');
    try {
      const saved = await unvoidPublicElevatorSurveyPhoto(token, photo.id);
      setVoidedPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      setPhotos((prev) => ({ ...prev, [saved.field_id]: saved }));
    } catch (e) {
      setPhotoError(e instanceof Error ? e.message : 'Failed to restore photo.');
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!token) return;
    if (missingPhotos.length > 0 && !confirmMissing) {
      setConfirmMissing(true);
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      // A PDF copy is filed with the customer's records; a rendering problem must
      // not cost the client the answers they just typed in.
      let pdf: string | undefined;
      try {
        pdf = await generateElevatorSurveyPdf(
          values,
          Object.entries(photos).map(([fieldId, photo]) => ({
            fieldId,
            url: publicElevatorSurveyPhotoUrl(token, photo.id),
            fileName: photo.original_file_name,
          })),
          { businessName: values['customer_property_name'] || info?.business_name || '' }
        );
      } catch {
        pdf = undefined;
      }
      await submitPublicElevatorSurvey(token, values, pdf);
      setSubmitted(true);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Failed to submit.');
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || photoBusy;

  return (
    <div style={wrapStyle}>
      <div style={cardStyle}>
        <h1 style={{ fontSize: '1.5rem', marginTop: 0, color: '#1f4b31' }}>Elevator Initial Survey</h1>
        {loading ? (
          <p>Loading…</p>
        ) : loadError ? (
          <p style={{ color: '#b91c1c' }}>{loadError}</p>
        ) : submitted ? (
          <div>
            <p style={{ fontSize: '1.05rem' }}>Thank you. Your elevator survey has been submitted.</p>
            <p style={{ color: '#6b7280' }}>Our team will review it and reach out to schedule your elevator appointment.</p>
          </div>
        ) : info ? (
          <>
            <p style={{ color: '#374151' }}>
              Please complete the details below about your elevator(s). This helps our team prepare for your installation.
            </p>
            {ELEVATOR_SURVEY_FORM.map((section) => (
              <section key={section.title}>
                <h2 style={sectionTitleStyle}>{section.title}</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.9rem 1.25rem' }}>
                  {section.fields.map((f) => (
                    <div key={f.id} style={{ gridColumn: f.id === 'property_address' || f.id === 'elevator_for_robotics' ? '1 / -1' : 'auto' }}>
                      <label style={labelStyle} htmlFor={`sv-${f.id}`}>
                        {f.label}
                      </label>
                      {f.type === 'yesno' ? (
                        <div style={{ display: 'flex', gap: '1.5rem', paddingTop: '0.3rem' }}>
                          {['Yes', 'No'].map((opt) => (
                            <label key={opt} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontWeight: 500 }}>
                              <input
                                type="radio"
                                name={f.id}
                                checked={values[f.id] === opt}
                                onChange={() => setValue(f.id, opt)}
                                disabled={busy}
                              />
                              {opt}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <input
                          id={`sv-${f.id}`}
                          type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'}
                          value={values[f.id] ?? ''}
                          onChange={(e) => setValue(f.id, e.target.value)}
                          disabled={busy}
                          style={inputStyle}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ))}

            <section>
              <h2 style={sectionTitleStyle}>Elevator Service Provider — Photos</h2>
              <p style={{ color: '#4b5563', marginTop: 0 }}>
                Please add a photo for each item below. You can take them with your phone or upload files from this device.
              </p>
              {photoError ? <p style={{ color: '#b91c1c' }}>{photoError}</p> : null}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                {ELEVATOR_SURVEY_PHOTOS.map((slot) => {
                  const photo = photos[slot.id];
                  const slotVoided = voidedPhotos.filter((p) => p.field_id === slot.id);
                  return (
                    <div key={slot.id} style={photoCardStyle}>
                      <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{slot.label}</span>
                      {photo && token ? (
                        <>
                          <img
                            src={publicElevatorSurveyPhotoUrl(token, photo.id)}
                            alt={slot.label}
                            style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }}
                          />
                          <span style={{ fontSize: '0.78rem', color: '#6b7280', wordBreak: 'break-all' }}>
                            {photo.original_file_name}
                          </span>
                          {voidSlot === slot.id ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                              <label style={{ ...labelStyle, marginBottom: 0 }} htmlFor={`void-reason-${slot.id}`}>
                                Why is this photo being voided?
                              </label>
                              <textarea
                                id={`void-reason-${slot.id}`}
                                style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                                value={voidReason}
                                onChange={(e) => setVoidReason(e.target.value)}
                                placeholder="e.g. Wrong elevator — photo is of the freight car"
                                disabled={photoBusy}
                              />
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                  type="button"
                                  style={{ ...smallButtonStyle, color: '#b91c1c' }}
                                  onClick={() => void handleVoidPhoto(slot.id)}
                                  disabled={busy}
                                >
                                  {photoBusy ? 'Voiding…' : 'Void photo'}
                                </button>
                                <button
                                  type="button"
                                  style={smallButtonStyle}
                                  onClick={() => {
                                    setVoidSlot(null);
                                    setVoidReason('');
                                  }}
                                  disabled={photoBusy}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button type="button" style={smallButtonStyle} onClick={() => setPhotoSlot(slot.id)} disabled={busy}>
                                Replace
                              </button>
                              <button
                                type="button"
                                style={{ ...smallButtonStyle, color: '#b91c1c' }}
                                onClick={() => {
                                  setPhotoError('');
                                  setVoidReason('');
                                  setVoidSlot(slot.id);
                                }}
                                disabled={busy}
                              >
                                Void
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div
                            style={{
                              height: 140,
                              borderRadius: 8,
                              border: '1px dashed #cbd2d9',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#9ca3af',
                              fontSize: '0.85rem',
                            }}
                          >
                            No photo yet
                          </div>
                          <button
                            type="button"
                            style={{ ...smallButtonStyle, borderColor: '#2f6f44', color: '#2f6f44', fontWeight: 600 }}
                            onClick={() => {
                              setPhotoError('');
                              setPhotoSlot(slot.id);
                            }}
                            disabled={busy}
                          >
                            Upload photo
                          </button>
                        </>
                      )}

                      {slotVoided.length > 0 && token
                        ? slotVoided.map((p) => (
                            <div
                              key={p.id}
                              style={{
                                display: 'flex',
                                gap: '0.5rem',
                                alignItems: 'flex-start',
                                paddingTop: '0.5rem',
                                borderTop: '1px dashed #e5e7eb',
                              }}
                            >
                              <a href={publicElevatorSurveyPhotoUrl(token, p.id)} target="_blank" rel="noreferrer">
                                <img
                                  src={publicElevatorSurveyPhotoUrl(token, p.id)}
                                  alt={`Voided: ${slot.label}`}
                                  style={{
                                    width: 48,
                                    height: 48,
                                    objectFit: 'cover',
                                    borderRadius: 6,
                                    border: '1px solid #e5e7eb',
                                    filter: 'grayscale(1)',
                                    opacity: 0.6,
                                  }}
                                />
                              </a>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#991b1b' }}>VOID</span>
                                {p.void_reason ? (
                                  <span style={{ display: 'block', fontSize: '0.72rem', color: '#991b1b' }}>
                                    {p.void_reason}
                                  </span>
                                ) : null}
                                <button
                                  type="button"
                                  style={{ ...smallButtonStyle, marginTop: '0.25rem', fontSize: '0.75rem' }}
                                  onClick={() => void handleUnvoidPhoto(p)}
                                  disabled={busy || Boolean(photo)}
                                  title={photo ? 'Void or replace the current photo first.' : undefined}
                                >
                                  Unvoid
                                </button>
                              </div>
                            </div>
                          ))
                        : null}
                    </div>
                  );
                })}
              </div>
            </section>

            {confirmMissing && missingPhotos.length > 0 ? (
              <div
                style={{
                  marginTop: '1.5rem',
                  border: '1px solid #f0c36d',
                  background: '#fdf6e3',
                  borderRadius: 8,
                  padding: '0.85rem 1rem',
                  color: '#8a6d1b',
                }}
              >
                <strong>{missingPhotos.length} photo{missingPhotos.length === 1 ? '' : 's'} still missing:</strong>{' '}
                {missingPhotos.map((p) => p.label).join(', ')}. You can add them now, or press Submit again to send the
                survey without them.
              </div>
            ) : null}

            {submitError ? <p style={{ color: '#b91c1c', marginTop: '1rem' }}>{submitError}</p> : null}

            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={busy}
              style={{ ...primaryButtonStyle, marginTop: '1.75rem', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1 }}
            >
              {submitting ? 'Submitting…' : confirmMissing && missingPhotos.length > 0 ? 'Submit anyway' : 'Submit Survey'}
            </button>
          </>
        ) : null}
      </div>

      {photoSlot ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17,24,39,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            zIndex: 50,
          }}
          onClick={() => !photoBusy && setPhotoSlot(null)}
        >
          <div
            style={{ background: 'white', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 440 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 0.35rem', color: '#1f4b31' }}>
              {ELEVATOR_SURVEY_PHOTOS.find((p) => p.id === photoSlot)?.label}
            </h3>
            <p style={{ margin: '0 0 1rem', color: '#6b7280', fontSize: '0.9rem' }}>
              Drag a photo into the box below, or browse for one on this device.
            </p>
            <FileDropZone
              accept="image/*"
              disabled={photoBusy}
              onFiles={(files) => void handlePhotoFiles(photoSlot, files)}
              maxFileSizeBytes={25 * 1024 * 1024}
              onReject={(msg) => setPhotoError(msg)}
              hint="JPG or PNG, up to 25 MB"
            />
            {photoError ? <p style={{ color: '#b91c1c', marginTop: '0.75rem' }}>{photoError}</p> : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" style={smallButtonStyle} onClick={() => setPhotoSlot(null)} disabled={photoBusy}>
                {photoBusy ? 'Uploading…' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ElevatorSurvey;
